import { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * Tracks which landing-page section is currently visible in the viewport
 * using IntersectionObserver. Returns the active section ID (e.g. 'services').
 * Only active when on the home page ('/').
 */
export const useActiveSection = (sectionIds: string[]) => {
  const [activeSection, setActiveSection] = useState<string | null>(null);
  const location = useLocation();

  useEffect(() => {
    // Only observe sections on the home page
    if (location.pathname !== '/') {
      setActiveSection(null);
      return;
    }

    const observers: IntersectionObserver[] = [];
    const visibleSections = new Map<string, number>();

    sectionIds.forEach((id) => {
      const el = document.getElementById(id);
      if (!el) return;

      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              visibleSections.set(id, entry.intersectionRatio);
            } else {
              visibleSections.delete(id);
            }

            // Pick the section with highest visibility
            if (visibleSections.size > 0) {
              let best: string | null = null;
              let bestRatio = 0;
              visibleSections.forEach((ratio, sectionId) => {
                if (ratio > bestRatio) {
                  bestRatio = ratio;
                  best = sectionId;
                }
              });
              setActiveSection(best);
            } else {
              setActiveSection(null);
            }
          });
        },
        {
          // Offset for the fixed navbar
          rootMargin: '-80px 0px -40% 0px',
          threshold: [0, 0.25, 0.5, 0.75, 1],
        }
      );

      observer.observe(el);
      observers.push(observer);
    });

    return () => {
      observers.forEach((obs) => obs.disconnect());
    };
  }, [sectionIds, location.pathname]);

  return activeSection;
};
